import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import {Button, Spinner, Stack, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import StandardDrawer from '../standard-drawer'
import {Checkbox} from '@chakra-ui/react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {useAuthHelper, AuthHelpers} from '@salesforce/commerce-sdk-react'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useBrand, toBrandCode} from '../../contexts/brand-context'

const DRAWER_HEADING_ID = 'close-account-drawer-heading'
const DATA_TEST_ID_PREFIX = 'close-account'

const CloseAccountModal = ({isOpen, onClose, onOpen}) => {
    const intl = useIntl()
    const {isGarage} = useBrand()
    const [isChecked, setIsChecked] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const navigate = useNavigation()
    const {brandId} = useBrand()
    const appOrigin = useAppOrigin()
    const logout = useAuthHelper(AuthHelpers.Logout)
    const {app: appConfig} = getConfig()
    const {getTokenWhenReady} = useAccessToken()
    const parameters = {
        ...appConfig.commerceAPI.parameters
    }
    const proxyPath = appConfig.commerceAPI.proxyPath
    const organizationId = parameters.organizationId
    const {site} = useMultiSite()
    const siteId = site?.id || parameters.siteId

    const onSignout = async () => {
        const token = await getTokenWhenReady()
        await fetch(`${appOrigin}/process-logout?siteId=${siteId}`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({token})
        })
        await logout.mutateAsync()
        navigate('/')
    }

    const handleCloseAccount = async () => {
        if (!isChecked) {
            setErrorMessage(
                intl.formatMessage({
                    id: 'use_registration.terms.and.conditions.error',
                    defaultMessage: 'Terms and conditions must be accepted'
                })
            )
            return
        }

        setIsLoading(true)
        try {
            const token = await getTokenWhenReady()
            const response = await fetch(
                `${proxyPath}/custom/sfid/v1/organizations/${organizationId}/close-account?siteId=${siteId}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        closeconsent: isChecked,
                        brand: toBrandCode(brandId)
                    })
                }
            )

            if (!response.ok) {
                const errorData = await response.json()
                throw new Error(errorData.message || 'Failed to close account.')
            }

            setErrorMessage('')
            onClose()
            onSignout()
        } catch (error) {
            setErrorMessage(
                intl.formatMessage({
                    id: 'close_account_modal.error_message',
                    defaultMessage: 'An unexpected error occurred.'
                })
            )
            setIsLoading(false)
        }
    }

    return (
        <StandardDrawer
            data-testid={`${DATA_TEST_ID_PREFIX}-drawer`}
            isOpen={isOpen}
            onClose={onClose}
            onOpen={onOpen}
            ariaLabelledBy={DRAWER_HEADING_ID}
        >
            <Stack spacing={6}>
                <Text as="h3" id={DRAWER_HEADING_ID} textStyle="webH3">
                    {isGarage ? (
                        <FormattedMessage
                            defaultMessage="Are you sure you want to delete your loyalty account?"
                            id="close_account_modal.title.garage"
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Are you sure you want to delete your loyalty account?"
                            id="close_account_modal.title.dynamite"
                        />
                    )}
                </Text>
                <Text textStyle="bodySmRegular">
                    {isGarage ? (
                        <FormattedMessage
                            defaultMessage="If you delete your account, you will lose access to your accumulated benefits and offers, your purchase history, and will be unsubscribed from our marketing communications for both Dynamite Collectif and Garage Collectif. Also, your personal data will be removed, where applicable. If you decide to rejoin our loyalty program in the future, you will need to create a new account."
                            id="close_account_modal.text.garage"
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="If you delete your account, you will lose access to your accumulated benefits and offers, your purchase history, and will be unsubscribed from our marketing communications for both Dynamite Collectif and Garage Collectif. Also, your personal data will be removed, where applicable. If you decide to rejoin our loyalty program in the future, you will need to create a new account."
                            id="close_account_modal.text.dynamite"
                        />
                    )}
                </Text>
                <Checkbox
                    data-testid={`${DATA_TEST_ID_PREFIX}-checkbox`}
                    isChecked={isChecked}
                    onChange={(e) => {
                        setIsChecked(e.target.checked)
                        if (errorMessage) setErrorMessage('')
                    }}
                    alignItems="flex-start"
                >
                    <Text textStyle="bodySmRegular">
                        <FormattedMessage
                            defaultMessage="I understand that by deleting my loyalty account, I will permanently lose access to my accumulated benefits and offers, and my purchase history. If I decide to rejoin the loyalty program in the future, I will need to create a new account."
                            id="close_account_modal.checkbox_text"
                        />
                    </Text>
                </Checkbox>

                {errorMessage && (
                    <Text color="orange.600" fontSize="sm" textAlign="center">
                        {errorMessage}
                    </Text>
                )}

                <Stack spacing={2}>
                    <Button
                        data-testid={`${DATA_TEST_ID_PREFIX}-delete-button`}
                        variant="outline"
                        onClick={handleCloseAccount}
                        isLoading={isLoading}
                        isDisabled={isLoading}
                        spinner={<Spinner size="sm" />}
                    >
                        <FormattedMessage
                            defaultMessage="Delete my account"
                            id="close_account_modal.close"
                        />
                    </Button>

                    <Button
                        data-testid={`${DATA_TEST_ID_PREFIX}-keep-button`}
                        variant="webPrimary"
                        onClick={onClose}
                    >
                        <FormattedMessage
                            defaultMessage="Keep My Account"
                            id="close_account_modal.keep"
                        />
                    </Button>
                </Stack>
            </Stack>
        </StandardDrawer>
    )
}

CloseAccountModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onOpen: PropTypes.func.isRequired
}

export default CloseAccountModal
